import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { 
  History, 
  UserCheck, 
  AlertCircle, 
  Plus, 
  Minus, 
  Search,
  RefreshCw,
  FileText,
  Wallet,
  ArrowUpCircle,
  ArrowDownCircle,
  Clock
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const FlexBankAudit = ({ isOpen, onClose, initialUserId = null }) => {
  const [loading, setLoading] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState(initialUserId || '');
  const [auditData, setAuditData] = useState(null);
  const [allAuditLogs, setAllAuditLogs] = useState([]);
  const [viewMode, setViewMode] = useState('employee'); // 'employee' or 'all'
  
  // Adjustment form state
  const [showAdjustForm, setShowAdjustForm] = useState(false);
  const [adjustmentBalance, setAdjustmentBalance] = useState('');
  const [adjustmentReason, setAdjustmentReason] = useState('');
  const [adjusting, setAdjusting] = useState(false);

  useEffect(() => {
    if (isOpen) {
      fetchEmployees();
      if (initialUserId) {
        setSelectedUserId(initialUserId);
        fetchUserAuditLog(initialUserId);
      }
    }
  }, [isOpen, initialUserId]);

  const fetchEmployees = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.get(`${API}/flex-bank/team`, { headers });
      setEmployees(response.data.team_balances || []);
    } catch (error) {
      console.error('Failed to fetch employees:', error);
      toast.error('Failed to load employees');
    }
  };

  const fetchUserAuditLog = async (userId) => {
    if (!userId) return;
    
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.get(`${API}/flex-bank/audit/${userId}`, { headers });
      setAuditData(response.data);
    } catch (error) {
      console.error('Failed to fetch audit log:', error);
      toast.error('Failed to load audit log');
    } finally {
      setLoading(false);
    }
  };

  const fetchAllAuditLogs = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.get(`${API}/flex-bank/audit/all?limit=100`, { headers });
      setAllAuditLogs(response.data.audit_logs || []);
    } catch (error) {
      console.error('Failed to fetch all audit logs:', error);
      if (error.response?.status === 403) {
        toast.error('Only admins can view all audit logs');
      } else {
        toast.error('Failed to load audit logs');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleEmployeeSelect = (userId) => {
    setSelectedUserId(userId);
    if (userId) {
      fetchUserAuditLog(userId);
      // Pre-fill adjustment balance with current balance
      const employee = employees.find(e => e.user_id === userId);
      if (employee) {
        setAdjustmentBalance(employee.flex_bank_balance.toString());
      }
    } else {
      setAuditData(null);
    }
  };

  const handleAdjustBalance = async () => {
    if (!selectedUserId || !adjustmentBalance || !adjustmentReason) {
      toast.error('Please fill in all fields');
      return;
    }

    const newBalance = parseInt(adjustmentBalance);
    if (isNaN(newBalance) || newBalance < 0) {
      toast.error('Please enter a valid balance (0 or greater)');
      return;
    }

    if (adjustmentReason.trim().length < 5) {
      toast.error('Please provide a more detailed reason (at least 5 characters)');
      return;
    }

    setAdjusting(true);
    try {
      const token = localStorage.getItem('token');
      const headers = { 
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json'
      };
      
      const response = await axios.post(
        `${API}/flex-bank/audit/adjust`,
        {
          user_id: selectedUserId,
          new_balance: newBalance,
          reason: adjustmentReason.trim()
        },
        { headers }
      );

      toast.success(`Balance adjusted: ${response.data.old_balance} → ${response.data.new_balance}`);
      
      // Refresh data
      fetchUserAuditLog(selectedUserId);
      fetchEmployees();
      setShowAdjustForm(false);
      setAdjustmentReason('');
    } catch (error) {
      console.error('Failed to adjust balance:', error);
      toast.error(error.response?.data?.detail || 'Failed to adjust balance');
    } finally {
      setAdjusting(false);
    }
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'N/A';
    // Append 'Z' to indicate UTC if not already present
    let utcTimestamp = timestamp;
    if (typeof timestamp === 'string' && !timestamp.endsWith('Z') && !timestamp.includes('+')) {
      utcTimestamp = timestamp + 'Z';
    }
    return new Date(utcTimestamp).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getActionLabel = (action) => {
    const labels = {
      'manual_adjustment': 'Manual Adjustment',
      'wfh_approval_deduction': 'WFH Approval',
      'year_reset': 'Annual Reset'
    };
    return labels[action] || action;
  };

  const getActionIcon = (change) => {
    if (change > 0) {
      return <ArrowUpCircle className="h-4 w-4 text-green-500" />;
    } else if (change < 0) {
      return <ArrowDownCircle className="h-4 w-4 text-red-500" />;
    }
    return <Clock className="h-4 w-4 text-gray-400" />;
  };

  const selectedEmployee = employees.find(e => e.user_id === selectedUserId);

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center space-x-2">
            <History className="h-5 w-5 text-blue-600" />
            <span>Flex Bank Audit Tool</span>
          </DialogTitle>
          <DialogDescription>
            View balance history and make adjustments with audit trail
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* View Mode Toggle */}
          <div className="flex space-x-2">
            <Button
              variant={viewMode === 'employee' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setViewMode('employee')}
            >
              <UserCheck className="h-4 w-4 mr-2" />
              By Employee
            </Button>
            <Button
              variant={viewMode === 'all' ? 'default' : 'outline'}
              size="sm"
              onClick={() => {
                setViewMode('all');
                fetchAllAuditLogs();
              }}
            >
              <FileText className="h-4 w-4 mr-2" />
              All Audit Logs
            </Button>
          </div>

          {viewMode === 'employee' ? (
            <>
              {/* Employee Selector */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Select Employee</Label>
                  <Select value={selectedUserId} onValueChange={handleEmployeeSelect}>
                    <SelectTrigger>
                      <SelectValue placeholder="Choose an employee..." />
                    </SelectTrigger>
                    <SelectContent>
                      {employees.map((emp) => (
                        <SelectItem key={emp.user_id} value={emp.user_id}>
                          {emp.name} ({emp.flex_bank_balance}/{emp.default_days} days)
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {selectedEmployee && (
                  <Card className="bg-blue-50 border-blue-200">
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-medium text-gray-900">{selectedEmployee.name}</p>
                          <p className="text-sm text-gray-600 capitalize">{selectedEmployee.role?.replace('_', ' ')}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-2xl font-bold text-blue-600">{selectedEmployee.flex_bank_balance}</p>
                          <p className="text-xs text-gray-500">of {selectedEmployee.default_days} days</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                )}
              </div>

              {/* Adjust Balance Button */}
              {selectedUserId && (
                <div className="flex justify-end">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setShowAdjustForm(!showAdjustForm);
                      if (selectedEmployee) {
                        setAdjustmentBalance(selectedEmployee.flex_bank_balance.toString());
                      }
                    }}
                    className="border-amber-300 text-amber-700 hover:bg-amber-50"
                  >
                    <Wallet className="h-4 w-4 mr-2" />
                    Adjust Balance
                  </Button>
                </div>
              )}

              {/* Adjustment Form */}
              {showAdjustForm && selectedUserId && (
                <Card className="border-amber-200 bg-amber-50">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base flex items-center space-x-2">
                      <AlertCircle className="h-4 w-4 text-amber-600" />
                      <span>Manual Balance Adjustment</span>
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>New Balance</Label>
                        <div className="flex items-center space-x-2">
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => setAdjustmentBalance(Math.max(0, parseInt(adjustmentBalance || 0) - 1).toString())}
                          >
                            <Minus className="h-4 w-4" />
                          </Button>
                          <Input
                            type="number"
                            min="0"
                            value={adjustmentBalance}
                            onChange={(e) => setAdjustmentBalance(e.target.value)}
                            className="w-20 text-center"
                          />
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => setAdjustmentBalance((parseInt(adjustmentBalance || 0) + 1).toString())}
                          >
                            <Plus className="h-4 w-4" />
                          </Button>
                        </div>
                        {selectedEmployee && (
                          <p className="text-xs text-gray-500">
                            Current: {selectedEmployee.flex_bank_balance} → New: {adjustmentBalance || '?'}
                            {adjustmentBalance && (
                              <span className={parseInt(adjustmentBalance) > selectedEmployee.flex_bank_balance ? 'text-green-600' : parseInt(adjustmentBalance) < selectedEmployee.flex_bank_balance ? 'text-red-600' : ''}>
                                {' '}({parseInt(adjustmentBalance) - selectedEmployee.flex_bank_balance >= 0 ? '+' : ''}{parseInt(adjustmentBalance) - selectedEmployee.flex_bank_balance})
                              </span>
                            )}
                          </p>
                        )}
                      </div>
                      <div className="space-y-2">
                        <Label>Reason for Adjustment *</Label>
                        <Textarea
                          value={adjustmentReason}
                          onChange={(e) => setAdjustmentReason(e.target.value)}
                          placeholder="e.g., Correction for duplicate deduction, Manager override, Data migration fix..."
                          rows={2}
                        />
                      </div>
                    </div>
                    <div className="flex justify-end space-x-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setShowAdjustForm(false);
                          setAdjustmentReason('');
                        }}
                      >
                        Cancel
                      </Button>
                      <Button
                        size="sm"
                        onClick={handleAdjustBalance}
                        disabled={adjusting || !adjustmentBalance || !adjustmentReason}
                        className="bg-amber-600 hover:bg-amber-700"
                      >
                        {adjusting ? 'Saving...' : 'Apply Adjustment'}
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Audit Log Table */}
              {loading ? (
                <div className="flex items-center justify-center py-8">
                  <RefreshCw className="h-6 w-6 animate-spin text-gray-400" />
                </div>
              ) : auditData ? (
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base">Balance History</CardTitle>
                    <CardDescription>
                      {auditData.audit_logs?.length || 0} audit entries for {auditData.user_name}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    {auditData.audit_logs && auditData.audit_logs.length > 0 ? (
                      <div className="space-y-2 max-h-[300px] overflow-y-auto">
                        {auditData.audit_logs.map((log, index) => (
                          <div 
                            key={log.id || index}
                            className="flex items-start justify-between p-3 bg-gray-50 rounded-lg border border-gray-100"
                          >
                            <div className="flex items-start space-x-3">
                              {getActionIcon(log.change)}
                              <div>
                                <p className="font-medium text-sm text-gray-900">
                                  {getActionLabel(log.action)}
                                </p>
                                <p className="text-xs text-gray-600">{log.reason}</p>
                                <p className="text-xs text-gray-400 mt-1">
                                  By {log.performed_by_name} • {formatTimestamp(log.timestamp)}
                                </p>
                              </div>
                            </div>
                            <div className="text-right">
                              <div className="flex items-center space-x-2">
                                <span className="text-sm text-gray-500">{log.old_balance}</span>
                                <span className="text-gray-400">→</span>
                                <span className="text-sm font-medium text-gray-900">{log.new_balance}</span>
                              </div>
                              <span className={`text-xs font-medium ${log.change > 0 ? 'text-green-600' : log.change < 0 ? 'text-red-600' : 'text-gray-500'}`}>
                                {log.change > 0 ? '+' : ''}{log.change} day(s)
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-8 text-gray-500">
                        <History className="h-12 w-12 mx-auto mb-2 text-gray-300" />
                        <p>No audit history found for this employee</p>
                        <p className="text-sm">Balance changes will appear here once recorded</p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              ) : selectedUserId ? (
                <div className="text-center py-8 text-gray-500">
                  <Search className="h-12 w-12 mx-auto mb-2 text-gray-300" />
                  <p>Loading audit data...</p>
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <UserCheck className="h-12 w-12 mx-auto mb-2 text-gray-300" />
                  <p>Select an employee to view their balance history</p>
                </div>
              )}
            </>
          ) : (
            /* All Audit Logs View */
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center justify-between">
                  <span>All Flex Bank Audit Logs</span>
                  <Button variant="outline" size="sm" onClick={fetchAllAuditLogs}>
                    <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                    Refresh
                  </Button>
                </CardTitle>
                <CardDescription>
                  Recent balance changes across all employees
                </CardDescription>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="flex items-center justify-center py-8">
                    <RefreshCw className="h-6 w-6 animate-spin text-gray-400" />
                  </div>
                ) : allAuditLogs.length > 0 ? (
                  <div className="space-y-2 max-h-[400px] overflow-y-auto">
                    {allAuditLogs.map((log, index) => (
                      <div 
                        key={log.id || index}
                        className="flex items-start justify-between p-3 bg-gray-50 rounded-lg border border-gray-100"
                      >
                        <div className="flex items-start space-x-3">
                          {getActionIcon(log.change)}
                          <div>
                            <p className="font-medium text-sm text-gray-900">
                              {log.user_name}
                            </p>
                            <p className="text-xs text-blue-600">{getActionLabel(log.action)}</p>
                            <p className="text-xs text-gray-600 mt-1">{log.reason}</p>
                            <p className="text-xs text-gray-400 mt-1">
                              By {log.performed_by_name} • {formatTimestamp(log.timestamp)}
                            </p>
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="flex items-center space-x-2">
                            <span className="text-sm text-gray-500">{log.old_balance}</span>
                            <span className="text-gray-400">→</span>
                            <span className="text-sm font-medium text-gray-900">{log.new_balance}</span>
                          </div>
                          <span className={`text-xs font-medium ${log.change > 0 ? 'text-green-600' : log.change < 0 ? 'text-red-600' : 'text-gray-500'}`}>
                            {log.change > 0 ? '+' : ''}{log.change} day(s)
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    <History className="h-12 w-12 mx-auto mb-2 text-gray-300" />
                    <p>No audit logs found</p>
                    <p className="text-sm">Balance changes will appear here once recorded</p>
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default FlexBankAudit;
